import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { UserService } from 'libs/user/user.service';
import { AuthorizationService } from 'libs/authorization/authorization.service';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/**
 * S2-02 — décide désormais contre les affiliations (AuthorizationService), pas
 * contre users/{id}.role directement.
 *
 * S3 — sélecteur de contexte : `resolveEffectiveRole` respecte le contexte
 * explicitement choisi (`ContextService`) pour une identité multi-rôle, au
 * lieu de retomber systématiquement sur la projection historique à un seul
 * rôle par priorité (qui bloquait par exemple `tutor/home` pour quelqu'un
 * dont l'apprentissage est prioritaire mais qui navigue en tuteur). Quand
 * plusieurs contextes existent et qu'aucun n'est choisi, `null` renvoie vers
 * l'écran de sélection plutôt que de deviner. Repli sur users/{id}.role
 * uniquement si l'identité n'a strictement aucune affiliation (ex. les
 * orphelins mesurés au sprint 0) : on ne bloque jamais un compte faute de
 * données migrées.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private authorizationService: AuthorizationService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const roles = route?.data?.roles as Array<string>;

    return this.userService.authGuardUser$.pipe(
      switchMap((user) => {
        if (!user && !state.url.includes('login')) {
          this.router.navigateByUrl('login');
          return of(false);
        }

        if (!user || !roles) {
          return of(true);
        }

        return this.authorizationService.resolveEffectiveRole(user.id, user.role).pipe(
          map((effectiveRole) => {
            if (!effectiveRole) {
              this.router.navigateByUrl('context-selector');
              return false;
            }
            if (!roles.includes(effectiveRole) || state.url.includes('login')) {
              this.router.navigateByUrl(`${effectiveRole}/home`);
              return false;
            }
            return true;
          }),
        );
      }),
    );
  }
}
